import React from 'react';
import { Badge, Tooltip } from 'antd';

import { ISeoMeta } from '../../../types/models/seo';
import { scoreSeoMeta, SeoMetaValues, CheckStatus, STATUS_COLOR } from './seoScore';

const BADGE_STATUS: Record<CheckStatus, 'success' | 'warning' | 'error'> = {
  pass: 'success',
  warn: 'warning',
  fail: 'error',
};

/**
 * Compact score for a table cell, with the failing checks in the tooltip — the same
 * shape as `DestinationContentStatus`, which is how content readiness is already
 * surfaced in these tables.
 *
 * Reads the single-locale `title`/`description` the list endpoint returns, so the score
 * reflects the default language. The panel scores each locale separately.
 */
const SeoScoreBadge = ({
  seoMeta,
  values,
}: {
  seoMeta?: ISeoMeta;
  values?: SeoMetaValues;
}) => {
  if (!seoMeta && !values) {
    return <Badge status="default" text="Not set" />;
  }

  const resolved: SeoMetaValues =
    values || {
      title: seoMeta?.title,
      description: seoMeta?.description,
      ogImage: seoMeta?.og_image,
      focusKeyword: seoMeta?.focus_keyword,
      noindex: seoMeta?.noindex,
    };

  const { score, checks, status } = scoreSeoMeta(resolved);
  const problems = checks.filter(check => check.status !== 'pass');

  return (
    <Tooltip
      title={
        problems.length ? (
          <div>
            {problems.map(check => (
              <div key={check.key} style={{ marginBottom: 2 }}>
                <span style={{ color: STATUS_COLOR[check.status] }}>●</span>{' '}
                <strong>{check.label}:</strong> {check.detail}
              </div>
            ))}
          </div>
        ) : (
          'All metadata checks pass.'
        )
      }
    >
      <span style={{ whiteSpace: 'nowrap' }}>
        <Badge status={BADGE_STATUS[status]} />
        <strong>{score}</strong>
        <span style={{ color: '#999' }}>/100</span>
        {!!problems.length && (
          <span style={{ color: '#999', marginLeft: 6, fontSize: '85%' }}>
            {problems.length} issue{problems.length === 1 ? '' : 's'}
          </span>
        )}
      </span>
    </Tooltip>
  );
};

export default SeoScoreBadge;
